import { computed, inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LiveStore } from '@features/live';
import { AuthStore } from '@core/auth';
import { SettingsStore } from '@core/settings';
import { PlayerService } from './player.service';

/**
 * The live timeline, shared by the full player controls and the compact (floating /
 * cast) controls.
 *
 * Both surfaces used to carry their own copies of these computeds, and they drifted: the
 * compact controls read `timeStart`/`timeEnd` off the play item ONLY, while a live channel
 * played through `/channels/{id}/play` carries neither — the payload is the channel, not
 * the event. So the floating player showed empty times, a disabled scrubber, and (via
 * `isControlDisabled`) disabled transport buttons for the whole of a live cast. One
 * implementation is what stops that drifting apart again.
 *
 * Everything here derives from PlayerService, and there is only ever one player, so a
 * single root-provided instance serves every surface.
 */
@Injectable({ providedIn: 'root' })
export class LiveTimelineService {
    private playerService = inject(PlayerService);
    private liveStore = inject(LiveStore);
    private authStore = inject(AuthStore);
    private settingsStore = inject(SettingsStore);

    private item = this.playerService.getItem();

    /**
     * The airing programme's own flags (`epgs/{id}/live-play`), only while they belong to the
     * channel that plays — a channel switch keeps the old ones until the new answer arrives.
     */
    private programmeFlags = computed(() => {
        const flags = this.playerService.getLiveProgrammeFlags()();
        if (!this.playerService.isCurrentItemLive() || !flags) return null;
        return flags.channelReferenceId === this.item()?.channelReferenceId ? flags : null;
    });

    /**
     * The event currently airing on the played channel.
     *
     * This is a lookup over programmes the guide has already loaded — it does not fetch.
     * When nothing has loaded them (a live channel started from a surface that never
     * opened the guide) this is null and the item fallbacks below take over.
     */
    public readonly currentEpg = computed(() => {
        const channelId = this.item()?.channelReferenceId;
        return channelId ? this.liveStore.liveOf(channelId) : null;
    });

    // EPG first, then the item: an item reached from the "Uzivo" catalogue carries the
    // event's own times, whereas a channel-play payload has none and only the guide knows
    // them. Returned as epoch-millisecond strings — what the timeline sliders bind to.
    public readonly liveTimeStart = computed(() => {
        const epgStart = this.currentEpg()?.timeStart;
        if (epgStart) return String(new Date(epgStart).getTime());
        const start = this.item()?.timeStart;
        return start ? String(new Date(start).getTime()) : undefined;
    });

    public readonly liveTimeEnd = computed(() => {
        const epgEnd = this.currentEpg()?.timeEnd;
        if (epgEnd) return String(new Date(epgEnd).getTime());
        const end = this.item()?.timeEnd;
        return end ? String(new Date(end).getTime()) : undefined;
    });

    // Pause / scrub permissions, shared so the full and the compact (cast) controls gate
    // alike — the cast receiver enforces the same live rules on its side (aviion-chromecast
    // applyLivePlaybackRules). The channel-play payload carries `startoverEnabled` /
    // `pauseEnabled`; `startOver` / `pauseLiveTv` are the old API's names, still honoured
    // for items that carry them. An item without either stays permissive. While live, the
    // airing programme's own flags apply on top (an unknown one doesn't restrict).
    public readonly isSeekEnabled = computed(() => {
        const item = this.item();
        const startover = item?.startoverEnabled ?? item?.startOver;
        const itemAllows = typeof startover != 'boolean' || startover;
        if (!this.playerService.isCurrentItemLive()) return itemAllows;
        return itemAllows && this.programmeFlags()?.startoverEnabled !== false && this.hasEpgHistory();
    });

    // Live scrubbing needs the operator to keep past EPG at all (`EPGDaysAvailablePast` >= 1).
    // Unset counts as kept — LiveStore assumes 7 days then.
    private hasEpgHistory = computed(() => {
        const days = parseInt(String(this.settingsStore.epgHistoryDaysLimit() ?? ''), 10);
        return !Number.isFinite(days) || days >= 1;
    });

    // "Start over" restarts the current live programme from the beginning — the
    // restartTvEnabled customer feature gates it while live.
    public readonly isRestartEnabled = computed(() => {
        if (!this.playerService.isCurrentItemLive()) return true;
        return this.authStore.customer()?.features?.restartTvEnabled ?? false;
    });

    // While live, pausing additionally needs the pauseLiveTvEnabled customer feature.
    public readonly isPauseEnabled = computed(() => {
        const item = this.item();
        const pause = item?.pauseEnabled ?? item?.pauseLiveTv;
        const itemAllows = typeof pause != 'boolean' || pause;
        if (!this.playerService.isCurrentItemLive()) return itemAllows;
        return itemAllows && this.programmeFlags()?.pauseEnabled !== false
            && (this.authStore.customer()?.features?.pauseLiveTvEnabled ?? false);
    });

    // Historic EPG (catch-up) — while live, rewinding into already-aired content is gated
    // by the historicEpgEnabled customer feature. VOD is unaffected.
    public readonly isHistoricEpgEnabled = computed(() => {
        if (!this.playerService.isCurrentItemLive()) return true;
        return this.authStore.customer()?.features?.historicEpgEnabled ?? false;
    });

    // Earliest live timestamp (ms) the customer may rewind to, bounded by historicEpgHours
    // behind the live edge. null = not live / no limit.
    public readonly historicEpgFloor = computed(() => {
        if (!this.playerService.isCurrentItemLive() || !this.isHistoricEpgEnabled()) return null;
        const hours = this.authStore.customer()?.features?.historicEpgHours;
        if (!hours) return null;
        return Date.now() - hours * 3600 * 1000;
    });

    // Timeline lower bound while live: the programme start, but never earlier than the
    // catch-up floor.
    public readonly liveSeekMin = computed(() => {
        const start = this.liveTimeStart();
        const floor = this.historicEpgFloor();
        if (floor == null || start == null) return start;
        return String(Math.max(Number(start), floor));
    });

    /** Playhead as a wall-clock timestamp (ms), for binding against the EPG bounds. */
    public readonly liveProgress = computed(() => {
        const progress = this.playerService.getProgress()();
        const seekRange = this.playerService.getSeekRange();
        const secondsBehindLive = seekRange.end - progress;

        return Date.now() - (secondsBehindLive * 1000);
    });

    /** At the live edge, within tolerance. Drives the LIVE / "return to live" control. */
    public readonly isLive = computed(() => {
        const progress = this.playerService.getProgress()();
        const seekRange = this.playerService.getSeekRange();
        return Math.abs(progress - seekRange.end) <= 3; //3sec tolerance
    });

    /**
     * Seeks to a wall-clock timestamp (ms), clamped to the DVR window and never past the
     * catch-up floor. Position 0 is meaningless on a live stream — the DVR window slides —
     * so every live seek, including "start over" and "return to live", goes through here.
     */
    public seekToTimestamp(value: number): Observable<void> {
        const floor = this.historicEpgFloor();
        if (floor != null && value < floor) {
            value = floor;
        }

        const seekRange = this.playerService.getSeekRange();
        const secondsBehindLive = (Date.now() - value) / 1000;

        let newPosition = seekRange.end - secondsBehindLive;

        if (newPosition < seekRange.start) {
            newPosition = seekRange.start;
        } else if (newPosition > seekRange.end) {
            newPosition = seekRange.end;
        }

        return this.playerService.setPosition(newPosition);
    }
}
